import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import apiClient from '../../../../../api/apiClient';
import { usePreloaderStore } from '../../../../../stores';
import Modal from '../../controls/Modal';
import LabeledInput from '../../controls/LabeledInput';
import { toLocalInput, toApiDateTime } from './ttUtils';

// Модалка создания/редактирования командного задания.
// Видимость студентам управляется датами: availableFrom (появление) и availableUntil (закрытие приёма).
export default function TaskModal({ task, smenaId, onSuccess, onClose }) {
    const editing = !!task;
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const [form, setForm] = useState({
        title: task?.title || '',
        description: task?.description || '',
        conditions: task?.conditions || '',
        rewardBalance: task?.rewardBalance ?? 50,
        coinBank: task?.coinBank ?? 300,
        availableFrom: toLocalInput(task?.availableFrom),
        availableUntil: toLocalInput(task?.availableUntil)
    });

    const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

    const rewardOk = form.rewardBalance !== '' && Number(form.rewardBalance) >= 0;
    const bankOk = form.coinBank !== '' && Number(form.coinBank) >= 0;
    const untilOk = !form.availableUntil || form.availableUntil > form.availableFrom;
    const valid = form.title.trim() && rewardOk && bankOk && form.availableFrom && untilOk;

    const buildPayload = () => ({
        title: form.title.trim(),
        description: form.description.trim() || null,
        conditions: form.conditions.trim() || null,
        rewardBalance: Number(form.rewardBalance),
        coinBank: Number(form.coinBank),
        availableFrom: toApiDateTime(form.availableFrom),
        availableUntil: toApiDateTime(form.availableUntil)
    });

    const save = async () => {
        if (!valid) return;
        show();
        try {
            if (editing) {
                await apiClient.patch(`/api/team-tasks/admin/update?id=${task.id}`, buildPayload());
            } else {
                await apiClient.post(`/api/team-tasks/admin/create?smenaId=${smenaId}`, buildPayload());
            }
            enqueueSnackbar('Сохранено', { variant: 'success' });
            onSuccess();
            onClose();
        } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Ошибка при сохранении задания', { variant: 'error' }); }
        finally { hide(); }
    };

    return (
        <Modal
            open
            kicker="Командное задание"
            title={editing ? 'Редактировать задание' : 'Новое командное задание'}
            onClose={onClose}
            footer={(
                <>
                    <button type="button" className="btn btn-ghost" onClick={onClose}>Отмена</button>
                    <button type="button" className="btn btn-primary" onClick={save} disabled={!valid}>Сохранить</button>
                </>
            )}
        >
            <LabeledInput label="Заголовок *" value={form.title} onChange={set('title')} placeholder="Флешмоб" />
            <div>
                <label className="ctl-label">Описание</label>
                <textarea className="textarea" value={form.description} onChange={set('description')} placeholder="Снимите общий танец команды" />
            </div>
            <div>
                <label className="ctl-label">Условия</label>
                <textarea className="textarea" value={form.conditions} onChange={set('conditions')} placeholder="Минимум 5 человек в кадре" />
            </div>
            <div className="tt-form-row">
                <LabeledInput label="Баллы каждому игроку команды *" type="number" value={String(form.rewardBalance)} onChange={set('rewardBalance')} />
                <LabeledInput label="Coin Limited на команду (делится поровну) *" type="number" value={String(form.coinBank)} onChange={set('coinBank')} />
            </div>
            <div className="tt-form-row">
                <LabeledInput label="Доступно с *" type="datetime-local" value={form.availableFrom} onChange={set('availableFrom')} />
                <LabeledInput label="Дедлайн приёма сдач" type="datetime-local" value={form.availableUntil} onChange={set('availableUntil')} />
            </div>
            {!untilOk && <div className="tt-form-err">Дедлайн должен быть позже даты «доступно с»</div>}
        </Modal>
    );
}

TaskModal.propTypes = {
    task: PropTypes.object,
    smenaId: PropTypes.number.isRequired,
    onSuccess: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};

TaskModal.defaultProps = { task: null };
